import Keycloak, {
  KeycloakInstance,
  KeycloakLoginOptions,
  KeycloakOnLoad,
  KeycloakProfile,
  KeycloakTokenParsed,
} from 'keycloak-js';
import LocalStorageKeys from '@/util/LocalStorageKeys';
import { ShopContext } from '@/util/ShopContext';
import OrderStore from '@/stores/order/order.store';
import store from '@/stores';
import { getModule } from 'vuex-module-decorators';

export default class KeycloakService {
  private static initialized = false;
  private static initFinished: Promise<boolean> | null = null;
  private static readonly keycloak: KeycloakInstance = Keycloak('/keycloak.json');

  public static updateLocalStorageToken(): string {
    const token = KeycloakService.keycloak.token!;
    window.localStorage.setItem(LocalStorageKeys.Token, token);
    window.localStorage.setItem(LocalStorageKeys.RefreshToken, KeycloakService.keycloak.refreshToken!);
    return token;
  }

  private static get refreshToken(): string {
    return window.localStorage.getItem(LocalStorageKeys.RefreshToken) || '';
  }

  public static updateToken(minValidity: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      KeycloakService.keycloak.updateToken(minValidity)
        .success(resolve)
        .error(reject);
    });
  }

  public static async login(options?: ExtendedLoginOptions): Promise<void> {
    await this.initOnce();
    return new Promise((resolve, reject) => {
      KeycloakService.keycloak.login(options)
        .success(resolve)
        .error(reject);
    });
  }

  public static initOnce(): Promise<boolean> {
    if (this.initFinished == null) {
      this.initFinished = this.doInit();
    }
    return this.initFinished;
  }

  /**
   * The following property controls which paths are restricted and needs a login
   */
  private static readonly restrictedPathMatcher: RegExp[] = ['^\/my\/.*$'].map((str) => new RegExp(str));

  static get authenticated(): boolean {
    return !!this.keycloak.authenticated;
  }

  static get profile(): KeycloakProfile | undefined {
    return this.keycloak.profile;
  }

  static get accessToken(): string {
    return window.localStorage.getItem(LocalStorageKeys.Token) || '';
  }

  static get parsedToken(): ExtendedKeycloakToken | null {
    return KeycloakService.initialized ? this.keycloak.tokenParsed || null : null;
  }

  static get isPathRestricted(): boolean {
    for (const regex of KeycloakService.restrictedPathMatcher) {
      if (regex.test(window.location.pathname)) {
        return true;
      }
    }
    return false;
  }

  public static logout() {
    // clear personal information from local storage
    getModule(OrderStore, store).resetState();
    KeycloakService.deleteToken();
    KeycloakService.keycloak.logout({
      redirectUri: `${window.location.protocol}//${window.location.host}/`,
    });
  }

  public static getChangePasswordUrl(locale: string): string {
    const passwordUrl = this.keycloak.createAccountUrl()
      .replace('/account', '/account/password');
    return KeycloakService.withExtraParameters(passwordUrl, {locale});
  }

  private static doInit(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      KeycloakService.extendKeycloakUrlCreators();
      KeycloakService.keycloak.init({
        onLoad: KeycloakService.getMode(),
        token: KeycloakService.accessToken,
        refreshToken: KeycloakService.refreshToken,
        timeSkew: 0,
      }).success((authenticated) => {
        KeycloakService.initialized = true;
        if (authenticated) {
          KeycloakService.updateLocalStorageToken();
        } else {
          KeycloakService.deleteToken();
        }
        resolve(authenticated);
      }).error(reject);
    });
  }

  private static extendKeycloakUrlCreators(): void {
    const originalLogin = KeycloakService.keycloak.createLoginUrl;
    KeycloakService.keycloak.createLoginUrl = (options?: ExtendedLoginOptions) => {
      return KeycloakService.withExtraParameters(originalLogin(options), {
        guestOrderUrl: options?.guestOrderUrl,
      });
    };
  }

  private static withExtraParameters(
    originalUrl: string, payload: { guestOrderUrl?: string, locale?: string },
  ): string {
    const {origin, pathname, search} = new URL(originalUrl);
    const params = new URLSearchParams(search);
    const language = payload.locale ?? localStorage.getItem(LocalStorageKeys.SelectedLanguage) ?? 'de-at';
    params.set('tenant', ShopContext.tenantCode);
    params.set('shop', ShopContext.shopCode);
    params.set('locale', language);
    params.set('kc_locale', language);    // causes Keycloak to persist the locale to the user's preferences as well
    if (payload.guestOrderUrl) {
      params.set('guestOrderUrl', payload.guestOrderUrl);
    }
    return `${origin}${pathname}?${params}`;
  }

  private static deleteToken() {
    window.localStorage.removeItem(LocalStorageKeys.Token);
    window.localStorage.removeItem(LocalStorageKeys.RefreshToken);
  }

  private static getMode(): KeycloakOnLoad {
    return KeycloakService.isPathRestricted ? 'login-required' : 'check-sso';
  }
}

export interface ExtendedLoginOptions extends KeycloakLoginOptions {
  guestOrderUrl?: string;
}

export interface ExtendedKeycloakToken extends KeycloakTokenParsed {
  email?: string;
  given_name?: string;
  family_name?: string;
  megacardRequestedAtRegister?: boolean;
}
